import React from 'react';
import {
  Card, CardContent, Typography, Avatar, Box, IconButton,
  Menu, MenuItem, useTheme, Chip, Divider, alpha, Stack,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { IAgent } from 'definitions/app/agent';

interface AgentCardProps {
  agent: IAgent;
  onEdit: (agent: IAgent) => void;
  onToggleActive: (mobile: string) => void;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, onEdit, onToggleActive }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const isActive = Boolean(agent.active);

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.35),
          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
        },
        borderTop: `3px solid ${isActive ? theme.palette.primary.main : theme.palette.grey[400]}`,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: isActive
                ? alpha(theme.palette.primary.main, 0.12)
                : alpha(theme.palette.grey[500], 0.15),
              color: isActive ? 'primary.main' : 'text.disabled',
              fontWeight: 700,
              width: 44,
              height: 44,
              fontSize: '1.1rem',
              flexShrink: 0,
            }}
          >
            {agent.name?.charAt(0)?.toUpperCase() ?? 'A'}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {agent.name}
              </Typography>
              <IconButton size="small" onClick={handleClick} sx={{ flexShrink: 0 }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
              <PhoneIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">
                {agent.mobile}
              </Typography>
            </Stack>

            {agent.address && (
              <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
                <LocationOnIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {agent.address}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.6rem',
              fontWeight: 700,
              bgcolor: isActive
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.grey[500], 0.15),
              color: isActive ? 'success.dark' : 'text.disabled',
            }}
          />
          {agent.fare !== undefined && (
            <Typography variant="caption" color="text.secondary">
              Fare: <strong>₹{agent.fare}</strong>
            </Typography>
          )}
        </Stack>
      </CardContent>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={() => { onEdit(agent); handleClose(); }}
          sx={{ fontSize: '0.875rem' }}
        >
          Edit Agent
        </MenuItem>
        <MenuItem
          onClick={() => { onToggleActive(agent.mobile); handleClose(); }}
          sx={{ fontSize: '0.875rem', color: isActive ? 'error.main' : 'success.main' }}
        >
          {isActive ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default AgentCard;
